import { useSyncExternalStore } from 'react';

/**
 * Cookie consent (§17 legal, and the prerequisite for GA4 in §19).
 *
 * ── Scope: this site has exactly ONE consentable cookie category ─────────────────────────────
 * Strictly necessary cookies — the Laravel session and the CSRF token — are exempt from consent
 * under the ePrivacy Directive and are set only in the admin, which is staff tooling behind a
 * login. The single non-essential technology on the public site is GA4. So consent here is one
 * decision, not a category matrix: a preferences panel with one toggle would be ceremony that
 * makes the choice harder to understand, not easier.
 *
 * If a second category ever lands (embedded video, a chat widget, ad pixels), this must become
 * per-category — the shape below is deliberately an object rather than a bare string so that
 * change does not have to invalidate everyone's stored answer.
 *
 * ── Why gating rather than GA4 Consent Mode ──────────────────────────────────────────────────
 * Consent Mode still contacts Google on every page load to send cookieless pings. Not loading
 * the script at all until consent is given is the stronger promise and the simpler one to
 * verify: with consent withheld there is no third-party request whatsoever.
 */

const STORAGE_KEY = 'visitsj.consent';

/** Bump when the meaning of a stored answer changes; an older version re-prompts. */
const VERSION = 1;

export type ConsentDecision = 'granted' | 'denied';

export type ConsentState = {
  analytics: ConsentDecision;
  version: number;
  /** ISO timestamp — GDPR expects the controller to be able to show WHEN consent was given. */
  decidedAt: string;
};

/** `null` = no decision recorded yet, so the banner is still owed. */
export type StoredConsent = ConsentState | null;

function read(): StoredConsent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed.version !== VERSION) return null;
    if (parsed.analytics !== 'granted' && parsed.analytics !== 'denied') return null;

    return { analytics: parsed.analytics, version: VERSION, decidedAt: parsed.decidedAt ?? '' };
  } catch {
    // Private mode, disabled storage, or hand-edited junk. Treat as undecided: the banner
    // reappearing is a nuisance, silently assuming consent is a compliance failure.
    return null;
  }
}

/*
 * A hand-rolled store rather than context: `useAnalytics` and the banner sit in different parts
 * of the tree, and `storage` events let a decision made in one tab apply in the others.
 */
let current: StoredConsent = read();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      current = read();
      emit();
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): StoredConsent {
  return current;
}

/** The recorded decision, or null while none has been made. */
export function useConsent(): StoredConsent {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

export function setConsent(analytics: ConsentDecision): void {
  const next: ConsentState = { analytics, version: VERSION, decidedAt: new Date().toISOString() };
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable — honour the choice for this session even if it cannot be persisted.
  }
  if (analytics === 'denied') revokeAnalytics();
  emit();
}

/** Reopen the banner so a decision can be changed — withdrawal must be as easy as consent. */
export function resetConsent(): void {
  current = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  revokeAnalytics();
  emit();
}

/**
 * The measurement id that gtag was actually loaded with, published by the analytics loader.
 *
 * Needed because withdrawal happens from the footer, which has no idea what the id is, and
 * Google's opt-out flag is per-id.
 */
let loadedMeasurementId: string | null = null;

export function rememberMeasurementId(id: string): void {
  loadedMeasurementId = id;
}

/**
 * Stop GA4 collecting and clear what it already set.
 *
 * Withdrawing consent has to do more than hide the banner: gtag.js cannot be unloaded once it
 * is on the page, so `ga-disable-<id>` (Google's documented opt-out flag) is what actually stops
 * further hits, and the `_ga*` cookies are removed so the identifier does not survive the
 * refusal. Without this, "Decline" after a previous "Accept" would be theatre.
 */
function revokeAnalytics(): void {
  try {
    /*
     * `ga-disable-<id>` needs the measurement id, which only the loader knows — hence the
     * registry above. Scanning `window` for existing `ga-disable-*` keys instead would be
     * circular: it can only re-disable what is already disabled, so the flag would never
     * actually be set and gtag would keep sending hits after a refusal.
     */
    if (loadedMeasurementId) {
      (window as unknown as Record<string, unknown>)[`ga-disable-${loadedMeasurementId}`] = true;
    }

    document.cookie
      .split(';')
      .map((c) => c.split('=')[0]?.trim())
      .filter((name): name is string => !!name && (name === '_ga' || name.startsWith('_ga_') || name === '_gid'))
      .forEach((name) => {
        // Cleared on the bare host and on the dot-prefixed domain, which is where GA sets them.
        const domains = [location.hostname, `.${location.hostname}`];
        domains.forEach((domain) => {
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
        });
        document.cookie = `${name}=; Max-Age=0; path=/`;
      });
  } catch {
    /* nothing actionable if the browser refuses */
  }
}
